import type { ComponentProps, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from '@/shared/ui/cx';
import { Spinner } from '@/shared/ui/Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'inverse';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** The class list a button-shaped thing wears, whatever element it turns out to be. */
function buttonClasses(
  variant: ButtonVariant,
  size: ButtonSize,
  block: boolean,
  extra?: string,
): string {
  return cx('btn', `btn--${variant}`, `btn--${size}`, block && 'btn--block', extra);
}

// ComponentProps rather than ButtonHTMLAttributes so `ref` is part of the props
// type — React 19 passes refs to function components as an ordinary prop.
export type ButtonProps = ComponentProps<'button'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the leading icon for a spinner and disables the button. */
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Stretches to the width of the container. */
  block?: boolean;
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  block = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClasses(variant, size, block, cx(loading && 'is-loading', className))}
      disabled={disabled || loading}
      // Announce the busy state rather than leaving screen readers with a
      // button that silently stopped responding.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 18 : 15} /> : iconLeft}
      {children ? <span className="btn__label">{children}</span> : null}
      {iconRight}
    </button>
  );
}

export type ButtonLinkProps = LinkProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
};

/**
 * A navigation that looks like a button.
 *
 * `Button` renders a `<button>` and has no polymorphic escape hatch, deliberately
 * — a control that submits and a control that navigates are different things to
 * a keyboard and to a screen reader, and one component that silently becomes
 * either is how the wrong one ships. So this is a separate export with the same
 * look, and it is a real `<Link>`: middle-click, cmd-click and "copy link
 * address" all work, which they never do on a button with an onClick.
 *
 * No `loading` prop: a link has nothing to wait for.
 */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  iconLeft,
  iconRight,
  block = false,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={buttonClasses(variant, size, block, className)} {...rest}>
      {iconLeft}
      {children ? <span className="btn__label">{children}</span> : null}
      {iconRight}
    </Link>
  );
}

export type IconButtonProps = Omit<ButtonProps, 'iconLeft' | 'iconRight' | 'block' | 'children'> & {
  /** Required — an icon-only control has no visible text to name it. */
  label: string;
  children: ReactNode;
};

export function IconButton({
  label,
  variant = 'ghost',
  size = 'md',
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <Button
      variant={variant}
      size={size}
      className={cx('btn--icon', className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </Button>
  );
}
